import type { FieldDefinition, FieldOption } from '@servicetim/shared';
import {
  useArchiveFieldOption,
  useCreateFieldOption,
  useFieldOptions,
  useReorderFieldOptions,
  useUpdateFieldOption,
} from '@servicetim/ui/api';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Label,
  LoadingState,
  ProblemBanner,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import { Archive, ArrowDown, ArrowUp, Check, LoaderCircle, Pencil, Plus, X } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { withProblemMessage } from '../common/server-errors';

const DEFAULT_COLOR = '#64748b';

/**
 * Options of a select field: add, rename, recolor, reorder (up/down) and archive. Archiving is
 * final on the API (`DELETE` = archive; values keep resolving); archived options stay listed.
 */
export function FieldOptionsDialog({
  field,
  onClose,
}: {
  field: FieldDefinition;
  onClose: () => void;
}) {
  const id = useId();
  const options = useFieldOptions(field.id);
  const create = useCreateFieldOption(field.id);
  const update = useUpdateFieldOption(field.id);
  const archive = useArchiveFieldOption(field.id);
  const reorder = useReorderFieldOptions(field.id);
  const confirm = useConfirm();
  const [error, setError] = useState<unknown>(null);
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);

  const { active, archived } = useMemo(() => {
    const all = [...(options.data ?? [])].sort((a, b) => a.position - b.position);
    return {
      active: all.filter((o) => o.archivedAt === null),
      archived: all.filter((o) => o.archivedAt !== null),
    };
  }, [options.data]);

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
      return true;
    } catch (caught) {
      setError(caught);
      return false;
    }
  };

  const move = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= active.length) return;
    const ids = active.map((o) => o.id);
    const [moved] = ids.splice(index, 1);
    if (moved === undefined) return;
    ids.splice(target, 0, moved);
    void run(() => reorder.mutateAsync({ ids }));
  };

  const onArchive = (option: FieldOption) =>
    void confirm({
      title: `Archive option "${option.label}"?`,
      description:
        'It disappears from pickers; tickets that hold it keep the value and the label still resolves. This cannot be undone.',
      confirmLabel: 'Archive',
      destructive: true,
      onConfirm: () => withProblemMessage(() => archive.mutateAsync(option.id)),
    });

  const busy = create.isPending || update.isPending || archive.isPending || reorder.isPending;

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Options of {field.label}</DialogTitle>
          <DialogDescription>
            Order here is the order in pickers. Tickets store option ids, so renaming is safe.
          </DialogDescription>
        </DialogHeader>

        {error ? <ProblemBanner error={error} size="sm" /> : null}

        {options.isPending ? (
          <LoadingState rows={3} />
        ) : options.error ? (
          <ProblemBanner error={options.error} onRetry={() => void options.refetch()} />
        ) : active.length === 0 && archived.length === 0 ? (
          <EmptyState size="sm" title="No options yet" description="Add the first option below." />
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border bg-surface-raised">
            {active.map((option, index) => (
              <OptionRow
                key={option.id}
                option={option}
                editing={editingId === option.id}
                onEdit={() => setEditingId(option.id)}
                onCancel={() => setEditingId(null)}
                onSave={async (patch) => {
                  if (await run(() => update.mutateAsync({ id: option.id, patch }))) {
                    setEditingId(null);
                  }
                }}
                onMoveUp={index > 0 ? () => move(index, -1) : undefined}
                onMoveDown={index < active.length - 1 ? () => move(index, 1) : undefined}
                onArchive={() => onArchive(option)}
                busy={busy}
              />
            ))}
            {archived.map((option) => (
              <li
                key={option.id}
                className="flex items-center gap-3 px-3 py-2 text-fg-muted"
                aria-label={`${option.label} (archived)`}
              >
                <span
                  aria-hidden="true"
                  className="size-3 rounded-full border border-border-strong"
                  style={{ backgroundColor: option.color ?? undefined }}
                />
                <span className="flex-1 truncate line-through">{option.label}</span>
                <Badge variant="outline">archived</Badge>
              </li>
            ))}
          </ul>
        )}

        <form
          className="flex flex-wrap items-end gap-2 rounded-md bg-surface-sunken p-3"
          onSubmit={async (event) => {
            event.preventDefault();
            const label = newLabel.trim();
            if (!label) return;
            const ok = await run(() => create.mutateAsync({ label, color: newColor || null }));
            if (ok) {
              setNewLabel('');
              setNewColor('');
              toast.success(`Option "${label}" added`);
            }
          }}
        >
          <div className="min-w-48 flex-1 space-y-1.5">
            <Label htmlFor={`${id}-new-label`}>New option</Label>
            <Input
              id={`${id}-new-label`}
              value={newLabel}
              maxLength={80}
              onChange={(event) => setNewLabel(event.target.value)}
              placeholder="Label"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${id}-new-color`}>Color</Label>
            <div className="flex items-center gap-1">
              <input
                id={`${id}-new-color`}
                type="color"
                className="size-9 cursor-pointer rounded-md border border-border-strong bg-surface-raised p-1"
                value={newColor || DEFAULT_COLOR}
                onChange={(event) => setNewColor(event.target.value)}
              />
              {newColor ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  aria-label="Clear color"
                  onClick={() => setNewColor('')}
                >
                  <X />
                </Button>
              ) : null}
            </div>
          </div>
          <Button type="submit" disabled={!newLabel.trim() || busy}>
            {create.isPending ? <LoaderCircle className="animate-spin" /> : <Plus />}
            Add
          </Button>
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface OptionRowProps {
  option: FieldOption;
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (patch: { label: string; color: string | null }) => Promise<void>;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onArchive: () => void;
  busy: boolean;
}

function OptionRow({
  option,
  editing,
  onEdit,
  onCancel,
  onSave,
  onMoveUp,
  onMoveDown,
  onArchive,
  busy,
}: OptionRowProps) {
  const id = useId();
  const [label, setLabel] = useState(option.label);
  const [color, setColor] = useState(option.color ?? '');

  if (editing) {
    return (
      <li className="flex flex-wrap items-center gap-2 px-3 py-2">
        <input
          type="color"
          aria-label={`Color of ${option.label}`}
          className="size-8 cursor-pointer rounded-md border border-border-strong bg-surface-raised p-0.5"
          value={color || DEFAULT_COLOR}
          onChange={(event) => setColor(event.target.value)}
        />
        <Input
          id={`${id}-label`}
          aria-label={`Label of ${option.label}`}
          value={label}
          maxLength={80}
          onChange={(event) => setLabel(event.target.value)}
          className="h-8 flex-1"
        />
        <Button
          type="button"
          size="sm"
          disabled={busy || !label.trim()}
          onClick={() => void onSave({ label: label.trim(), color: color || null })}
        >
          <Check /> Save
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </li>
    );
  }

  return (
    <li className="flex items-center gap-3 px-3 py-2">
      <span
        aria-hidden="true"
        className="size-3 rounded-full border border-border-strong"
        style={{ backgroundColor: option.color ?? undefined }}
      />
      <span className="flex-1 truncate text-fg text-sm">{option.label}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        aria-label={`Move ${option.label} up`}
        disabled={!onMoveUp || busy}
        onClick={onMoveUp}
      >
        <ArrowUp />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        aria-label={`Move ${option.label} down`}
        disabled={!onMoveDown || busy}
        onClick={onMoveDown}
      >
        <ArrowDown />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        aria-label={`Edit ${option.label}`}
        disabled={busy}
        onClick={onEdit}
      >
        <Pencil />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8 text-red-600 dark:text-red-400 hover:text-red-700 dark:text-red-300"
        aria-label={`Archive ${option.label}`}
        disabled={busy}
        onClick={onArchive}
      >
        <Archive />
      </Button>
    </li>
  );
}
